/**
 * ProfilePickerDialog lets whoever's holding a shared-screen controller switch to a
 * different device profile (see ../../../../deps/localMates/deviceProfiles), or
 * create a new one - separate from editing a profile's name/thumbnail (see
 * NameAndAvatarTab). Every controller already gets a profile auto-provisioned the
 * moment it connects (see controllerMates.ts), so this only ever opens on demand via
 * deviceProfiles.requestPicker (the "Switch Profile" action) - never a mandatory gate.
 */
import * as React from 'react'
import { useSignals } from '@preact/signals-react/runtime'
import { cn, getInitials, Avatar, AvatarFallback, Button } from '@merels/ui'
import { ActionButton } from '../../../library/ActionButton/ActionButton'
import { useListNavigation } from '../../../../hooks/useListNavigation'
import type { DeviceProfile, DeviceProfiles } from '../../../../deps/localMates/deviceProfiles/deviceProfiles.types'

export interface ProfilePickerDialogProps {
  deviceProfiles: DeviceProfiles
}

export function ProfilePickerDialog({ deviceProfiles }: ProfilePickerDialogProps) {
  useSignals()
  const [newProfileName, setNewProfileName] = React.useState('')

  const controllerId = deviceProfiles.pickerRequestControllerId.value
  const open = Boolean(controllerId)

  const profiles = deviceProfiles.profiles.value

  React.useEffect(() => {
    if (open) setNewProfileName('')
  }, [open, controllerId])

  const handleSelect = React.useCallback(
    (profile: DeviceProfile) => {
      if (!controllerId) return
      deviceProfiles.assignProfile(controllerId, profile.profileId)
      deviceProfiles.clearPickerRequest()
    },
    [controllerId, deviceProfiles]
  )

  const nav = useListNavigation(profiles, handleSelect, open)

  if (!open || !controllerId) return null

  const handleCreate = (e: React.FormEvent) => {
    e.preventDefault()
    const name = newProfileName.trim()
    if (!name) return
    const created = deviceProfiles.createProfile(name)
    deviceProfiles.assignProfile(controllerId, created.profileId)
    deviceProfiles.clearPickerRequest()
  }

  return (
    <div className="fixed inset-0 bg-overlay flex items-center justify-center z-[10000]">
      <div className="bg-surface rounded-lg p-6 w-full max-w-md shadow-lg">
        <div className="flex items-center justify-between mb-1">
          <h3 className="text-lg font-semibold text-primary">Switch Profile</h3>
          <Button variant="outline" size="sm" onClick={() => deviceProfiles.clearPickerRequest()}>
            Cancel
          </Button>
        </div>
        <p className="text-xs text-muted mb-4">Controller {controllerId}</p>

        {profiles.length > 0 && (
          <div className="flex flex-col gap-2 mb-4">
            {profiles.map((p, i) => (
              <button
                key={p.profileId}
                ref={(el) => nav.registerItem(i, el)}
                type="button"
                onClick={() => handleSelect(p)}
                className={cn(
                  'flex items-center gap-3 p-2 rounded-lg border border-basic hover:bg-subtle transition-colors text-left cursor-pointer focus:outline-none',
                  nav.focusedIndex === i && 'ring-2 ring-accent'
                )}
              >
                <Avatar className="h-8 w-8">
                  <AvatarFallback className="bg-accent/20 text-primary text-xs font-bold">
                    {getInitials(p.name)}
                  </AvatarFallback>
                </Avatar>
                <span className="text-sm font-medium text-primary">{p.name}</span>
              </button>
            ))}
          </div>
        )}

        <form onSubmit={handleCreate} className="flex gap-2">
          <input
            type="text"
            value={newProfileName}
            onChange={(e) => setNewProfileName(e.target.value)}
            placeholder="New profile name"
            autoFocus={profiles.length === 0}
            className="flex-1 p-2 border-2 border-basic rounded-lg text-sm bg-surface text-primary placeholder:text-muted focus:outline-none focus:ring-1 focus:ring-accent transition-colors"
          />
          <ActionButton type="submit" variant="default" accentBackground>
            Create
          </ActionButton>
        </form>
      </div>
    </div>
  )
}
